import PathEntry from "@/models/PathEntry";
import Icon from "@/plugins/icons";
import i18n from "@/plugins/i18n";
import {RawLocation} from "vue-router";
import {AttachmentMediaSize, CategoryState, CategoryView, Routes} from "@/helpers/constants";
import {
	ICategoryAdmin,
	ICategoryAdminListEntry,
	ICategoryAdminPathEntry,
	ICategoryAdminSimple
} from "@/models/category/CategoryAdminModels";
import {
	ICategoryShopListEntry,
	ICategoryShopPathEntry,
	ICategoryShopSimple
} from "@/models/category/CategoryShopModels";
import TranslatedValueHelper from "@/models/translated-value/TranslatedValueHelper";
import AttachmentHelper from "@/models/attachment/AttachmentHelper";
import store from "@/store";
import StringHelper from "@/helpers/StringHelper";
import {ITranslatedValue} from "@/models/translated-value/TranslatedValueModels";
import {FakeItemShopListEntry} from "@/models/item/FakeItemShopListEntry";

export default class CategoryHelper {

	public static detailRoute(canonical: string): RawLocation {
		return {
			name: Routes.category,
			params: {
				canonical: canonical,
			},
		};
	}

	public static detailRouteAdmin(id: string): RawLocation {
		return {
			name: Routes.adminCategory,
			params: {
				id: id,
			},
		};
	}

	public static avatarUrl(model: ICategoryAdminSimple | ICategoryShopSimple, size: AttachmentMediaSize): string | null {
		const catWithAvatar = this.categoryPath(model).reverse().find(c => c.avatarAttachmentId !== null);
		if (catWithAvatar === undefined) return null;

		const avatar = catWithAvatar.galleryAttachments.find(a => a.id === catWithAvatar.avatarAttachmentId);
		return avatar === undefined ? null : AttachmentHelper.getImageUrl(avatar, size);
	}

	public static colorHierarchical(model: ICategoryAdminSimple | ICategoryShopSimple): string | null {
		let color = this.categoryPath(model).reverse().find(c => c.color !== null)?.color ?? null;
		if (color !== null) return color;
		color = getComputedStyle(document.documentElement).getPropertyValue('--v-primary-base').trim();
		return StringHelper.isEmpty(color) ? null : color;
	}

	public static sort(a: ICategoryShopListEntry | ICategoryAdminListEntry, b: ICategoryShopListEntry | ICategoryAdminListEntry) {
		const nameA = TranslatedValueHelper.get(a.name, i18n.locale);
		const nameB = TranslatedValueHelper.get(b.name, i18n.locale);
		if (nameA === null && nameB === null) return 0;
		if (nameA !== null && nameB === null) return -1;
		if (nameA === null && nameB !== null) return 1;
		if (nameA !== null && nameB !== null) return nameA.localeCompare(nameB);
		return 0;
	}

	public static pathIcon(): string {
		return Icon.category;
	}

	public static pathLabel(model: ICategoryAdminPathEntry | ICategoryShopPathEntry): string {
		return TranslatedValueHelper.get(model.name, i18n.locale, true) ?? '?';
	}

	public static pathEntry(model: ICategoryAdminPathEntry | ICategoryShopPathEntry, adminRoute: boolean = false): PathEntry {
		return new PathEntry(this.pathIcon(), this.pathLabel(model),
			adminRoute
				? this.detailRouteAdmin(model.id)
				: this.detailRoute(this.currentCanonical(model.canonical)));
	}

	public static path(model: ICategoryAdminSimple | ICategoryShopSimple, adminRoutes: boolean = false): PathEntry[] {
		return model.path.map(c => CategoryHelper.pathEntry(c, adminRoutes));
	}

	public static isImported(model: ICategoryAdmin | null): boolean {
		return model === null || model.externalId !== null;
	}

	public static stateColor(model: ICategoryAdminSimple | ICategoryShopSimple): string {
		switch (model.state) {
			case CategoryState.active:
				return 'success';
			case CategoryState.archived:
			default:
				return 'error';
		}
	}

	public static stateIcon(model: ICategoryAdminSimple | ICategoryShopSimple): string {
		switch (model.state) {
			case CategoryState.active:
				return Icon.unarchive;
			case CategoryState.archived:
			default:
				return Icon.archive;
		}
	}

	public static booleanIcon(value: boolean): string {
		switch (value) {
			case true:
				return Icon.yes;
			case false:
			default:
				return Icon.no;
		}
	}

	public static viewLabel(value: CategoryView): string {
		switch (value) {
			case CategoryView.cardView:
				return i18n.t('constants.card').toString();
			case CategoryView.tableView:
				return i18n.t('constants.table').toString();
			default:
				return "";
		}
	}

	public static defaultSortOptions() {
		return Object.keys(new FakeItemShopListEntry()).map(key => ({
			text: i18n.t('item.' + key).toString(),
			value: key,
		})).sort((e1, e2) => e1.text.localeCompare(e2.text));
	}

	public static defaultSortByLabel(filterValue: string) {
		return this.defaultSortOptions().filter(entry => entry.value === filterValue)[0]?.text ?? '-';
	}

	public static currentCanonical(canonical: ITranslatedValue, locale: string | null = null): string {
		return TranslatedValueHelper.get(canonical, locale ?? i18n.locale) ?? '';
	}

	public static parentCategory(model: ICategoryAdminSimple | ICategoryShopSimple): ICategoryAdminPathEntry | ICategoryShopPathEntry {
		return model.path
			.reduce((parent, c) =>
				c.sortIndex > parent.sortIndex ? c : parent);
	}

	public static sortedPath(model: ICategoryAdminSimple | ICategoryShopSimple, adminRoutes: boolean = false, includeSelf: boolean = false): PathEntry[] {
		// Create Shallow Copy in Order to prevent rerender because of sort()
		return [...model.path]
			.sort((a: ICategoryShopPathEntry | ICategoryAdminPathEntry, b: ICategoryShopPathEntry | ICategoryAdminPathEntry) =>
				a.sortIndex - b.sortIndex)
			.concat(includeSelf ? [model] : [])
			.map(c => CategoryHelper.pathEntry(c, adminRoutes));
	}

	private static categoryPath(model: ICategoryAdminSimple | ICategoryShopSimple, includeSelf: boolean = true): (ICategoryAdminListEntry | ICategoryShopListEntry)[] {
		return model.path
			.map(c => store.getters['category/categoryById'](c.id))
			.filter(c => c !== null)
			.concat(includeSelf ? [model] : []);
	}

}
